// https://projects.invisionapp.com/d/main#/console/19025613/400363205/preview

// pie chart implementation informed by
// app/static/frontend/visualizations/components/PieChart
// app/static/frontend/profiles-new/components/map/GeoShapeMap
// https://observablehq.com/@d3/pie-chart
// https://www.d3-graph-gallery.com/graph/pie_annotation.html
// http://demo.interface.club/limitless/demo/Template/layout_1/LTR/default/full/d3_pies.html
// http://demo.interface.club/limitless/demo/Template/global_assets/js/demo_charts/d3/pies/pie_basic.js
// http://jsfiddle.net/tywnd2q3/3/

import React, { useRef } from "react"
import PropTypes from "prop-types"

// components
import { Legend } from "QuorumDesign"

// d3 renderers
import { useRenderSvg } from "app/static/frontend/widgets/components/visualization/renderers/sharedRenderers"

import {
    arcRenderer,
    pieRenderer,
    renderPie,
} from "app/static/frontend/widgets/components/visualization/renderers/pie/pieRenderers"

// hooks
import { useRefDimensions } from "app/static/frontend/widgets/components/visualization/hooks/visualizationHooks"

export const Pie = (props) => {
    const pie = useRef(null)

    const { height, width } = useRefDimensions({
        reference: pie,
        timestamp: props.timestamp,
    })
    const [heightMargin, widthMargin] = [
        height - (props.margin.top + props.margin.bottom),
        width - (props.margin.left + props.margin.right),
    ]

    const renderPieNodes = () => {
        const arcRendererMemo = arcRenderer({
            height: heightMargin,
            width: widthMargin,
        })
        const arcLabelRendererMemo = arcRenderer({
            arcLabel: true,
            height: heightMargin,
            width: widthMargin,
        })
        const pieRendererMemo = pieRenderer({
            data: props.data,
            timestamp: props.timestamp,
        })

        renderPie({
            arcRendererMemo,
            arcLabelRendererMemo,
            colors: props.colors,
            dataSources: props.dataSources,
            editing: props.editing,
            editForm: props.editForm,
            height: heightMargin,
            isExternal: props.isExternal,
            pieRendererMemo,
            reference: pie,
            // for segueing from Pie onClick to safedSearch selection in search
            showInSearch: props.showInSearch,
            timestamp: props.timestamp,
            width: widthMargin,
        })
    }

    useRenderSvg({
        height,
        isPie: true,
        reference: pie,
        width,
    })

    renderPieNodes()

    return (
        <React.Fragment>
            <svg
                data-cy="pie-visualization-widget"
                ref={pie}
                // ensure that the Pie svg stretches to the height of the parent div
                // this is important because we want the onResize to set correct values
                // immediately after mount
                style={{
                    flexGrow: 1,
                }}
            />
            {props.colors && props.data && <Legend colors={props.colors} data={props.labels} />}
        </React.Fragment>
    )
}

Pie.defaultProps = {
    // TODO: dummy colors
    // this will eventually come from the ColorPickerField(s) component
    colors: {
        0: "#A95AA1",
        1: "#503ABD",
        2: "#85C0F9",
    },
    // TODO: dummy data
    // this will eventually come from the FieldArray (index), labels (name), and data (value) from the evaluated SafedSearch
    data: [
        { name: "Science", value: 52 },
        { name: "Solar Cars", value: 25 },
        { name: "Engineering", value: 23 },
    ],
    margin: {
        top: 25,
        right: 15,
        bottom: 25,
        left: 15,
    },
}
Pie.propTypes = {
    colors: PropTypes.objectOf(PropTypes.string),
    data: PropTypes.array,
    dataSources: PropTypes.array,
    editing: PropTypes.bool,
    editForm: PropTypes.bool,
    isExternal: PropTypes.bool,
    labels: PropTypes.array,
    margin: PropTypes.object,
    // the timestamp is used for memoization in place of props.data
    // because it is less computationally intensive to compare
    timestamp: PropTypes.number,

    // action creators
    showInSearch: PropTypes.func,
}

export default Pie
